import { PanelError, bodyOf, httpStatusOf, isOutcomeUnknown, toPanelError } from '@/console/api/errors'
import {
  McisCutoverRollbackResendOutcome,
  McisCutoverRollbackResendRequest,
  McisCutoverRollbackResendResponse,
  McisCutoverRollbackResendRow,
  McisCutoverRollbackResendRowStatus,
} from '@/console/api/types'

/**
 * MCIS 컷오버 롤백 재송신 패널의 순수 로직(LOGISPRD-14487). 화면 컴포넌트는 이 모듈이 계산한 값만 그린다.
 * 입력 검증은 서버 400 규칙(`McisCutoverRollbackResendAdminRequests.kt`)과 같게 맞춰 화면에서 먼저 끊는다.
 */
export const MAX_OUTBOUND_RESULT_ERP_IDS = 100
export const MAX_REASON_LENGTH = 500
/** 100건 결과 표가 끝없이 길어지지 않게 하는 높이(px). 글로벌 입고 롤백 표와 같다. */
export const ROW_TABLE_MAX_HEIGHT = 480

/** 운영 절차의 정본. SQL·중단 조건을 콘솔에 복사하지 않고 이 문서로 보낸다. */
export const RUNBOOK_URL = 'https://github.com/musinsa/moms-be/blob/main/docs/operations/runbooks/mcis-cutover-rollback-resend.md'

/** 서버 `NO_CONTROL_CHARS_REGEX` 와 같은 집합. 감사 로그 한 줄 계약을 지킨다. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\r\n\t\u0000-\u001f\u0085\u2028\u2029]/

const ID_TOKEN = /^\d+$/

export const ROW_STATUS = {
  ELIGIBLE: 'ELIGIBLE',
  ALREADY_RELEASED: 'ALREADY_RELEASED',
  NOT_MCIS_CUTOVER: 'NOT_MCIS_CUTOVER',
  NOT_FOUND: 'NOT_FOUND',
} as const satisfies Record<McisCutoverRollbackResendRowStatus, McisCutoverRollbackResendRowStatus>

export const OUTCOME = {
  DRY_RUN: 'DRY_RUN',
  APPLIED: 'APPLIED',
  NO_OP: 'NO_OP',
  REJECTED_CUTOVER_ENABLED: 'REJECTED_CUTOVER_ENABLED',
} as const satisfies Record<McisCutoverRollbackResendOutcome, McisCutoverRollbackResendOutcome>

export type ResendTone = 'good' | 'warn' | 'crit' | 'neutral' | 'strong'

export const ROW_STATUS_PRESENTATION = {
  ELIGIBLE: { label: '실행 대상', tone: 'strong', meaning: 'API_FLAG=1 · MCIS_CUTOVER 행이에요. 실행하면 API_FLAG=0 으로 되돌려요' },
  ALREADY_RELEASED: { label: '이미 복원됨', tone: 'neutral', meaning: '이미 API_FLAG=0 이에요. 배치가 집을 차례라 조치가 필요 없어요' },
  NOT_MCIS_CUTOVER: { label: '컷오버 행 아님', tone: 'crit', meaning: '컷오버로 마감한 행이 아니에요. MCIS 와 다시 대사하세요' },
  NOT_FOUND: { label: '행 없음', tone: 'crit', meaning: '그 id 로 OIF_LO_GI 행을 찾지 못했어요. MCIS 와 다시 대사하세요' },
} satisfies Record<McisCutoverRollbackResendRowStatus, { label: string; tone: ResendTone; meaning: string }>

export const ROW_STATUS_ORDER = Object.keys(ROW_STATUS_PRESENTATION) as McisCutoverRollbackResendRowStatus[]

export const OUTCOME_PRESENTATION = {
  DRY_RUN: { label: '판정', tone: 'neutral' },
  APPLIED: { label: '복원함', tone: 'good' },
  NO_OP: { label: '바뀐 행 없음', tone: 'warn' },
  REJECTED_CUTOVER_ENABLED: { label: '거절 · 컷오버 flag ON', tone: 'crit' },
} satisfies Record<McisCutoverRollbackResendOutcome, { label: string; tone: ResendTone }>

export type ParsedOutboundResultErpIds = {
  /** 중복을 뺀 오름차순 id. */
  ids: number[]
  /** 양의 정수가 아니거나 안전한 정수 범위를 넘는 값. 하나라도 있으면 판정을 막는다. */
  invalid: string[]
  duplicates: number
  overLimit: boolean
}

const isValidId = (token: string): boolean => {
  if (!ID_TOKEN.test(token)) return false
  const id = Number(token)
  return id > 0 && Number.isSafeInteger(id)
}

/** 쉼표·공백·줄바꿈으로 나눈다. 100건 상한은 중복 제거 후 기준이다(서버 `Set` 과 같다). */
export const parseOutboundResultErpIds = (raw: string): ParsedOutboundResultErpIds => {
  const tokens = raw.split(/[\s,]+/).filter((token) => token.length > 0)
  const invalid = tokens.filter((token) => !isValidId(token))
  const validIds = tokens.filter(isValidId).map(Number)
  const ids = [...new Set(validIds)].sort((left, right) => left - right)

  return {
    ids,
    invalid: [...new Set(invalid)],
    duplicates: validIds.length - ids.length,
    overLimit: ids.length > MAX_OUTBOUND_RESULT_ERP_IDS,
  }
}

export type ReasonProblem = 'EMPTY' | 'TOO_LONG' | 'CONTROL_CHARS'

export const REASON_PROBLEM_MESSAGE = {
  EMPTY: '사유를 적어야 판정할 수 있어요.',
  TOO_LONG: `사유는 ${MAX_REASON_LENGTH}자까지예요.`,
  CONTROL_CHARS: '사유에 줄바꿈·탭 같은 제어문자를 넣을 수 없어요.',
} satisfies Record<ReasonProblem, string>

/** 앞뒤 공백을 뺀 값으로 검사한다. 서버로도 그 값을 보낸다. */
export const reasonProblemOf = (raw: string): ReasonProblem | null => {
  const reason = raw.trim()
  if (reason.length === 0) return 'EMPTY'
  if (reason.length > MAX_REASON_LENGTH) return 'TOO_LONG'
  if (CONTROL_CHARS.test(reason)) return 'CONTROL_CHARS'
  return null
}

/** 판정·실행이 공유하는 입력. id 는 정렬돼 있어 입력 순서만 바꾼 경우 같은 입력으로 본다. */
export type ResendInput = { outboundResultErpIds: number[]; reason: string }

/** 유효하지 않은 값이 하나라도 섞이면 null — 일부만 보내면 담당자는 전부 확인했다고 믿게 된다. */
export const toResendInput = (parsed: ParsedOutboundResultErpIds, rawReason: string): ResendInput | null => {
  if (parsed.ids.length === 0 || parsed.invalid.length > 0 || parsed.overLimit) return null
  if (reasonProblemOf(rawReason) !== null) return null
  return { outboundResultErpIds: parsed.ids, reason: rawReason.trim() }
}

export const sameInput = (left: ResendInput, right: ResendInput): boolean =>
  left.reason === right.reason &&
  left.outboundResultErpIds.length === right.outboundResultErpIds.length &&
  left.outboundResultErpIds.every((id, index) => id === right.outboundResultErpIds[index])

export const toRequest = (input: ResendInput, dryRun: boolean): McisCutoverRollbackResendRequest => ({ ...input, dryRun })

/** 판정 한 번의 입력과 응답. 실행은 이 입력을 그대로 dryRun=false 로 다시 보낸다. */
export type ResendJudgement = { input: ResendInput; response: McisCutoverRollbackResendResponse }

export type ExecutionBlocker = 'STALE' | 'NOT_DRY_RUN' | 'CUTOVER_ENABLED' | 'NEEDS_RECONCILIATION' | 'NOTHING_ELIGIBLE' | 'IN_FLIGHT'

export const EXECUTION_BLOCKER_MESSAGE = {
  STALE: '판정 뒤 id 나 사유가 바뀌었어요. 다시 판정해야 실행할 수 있어요.',
  NOT_DRY_RUN: '판정 응답이 아니에요. 다시 판정하세요.',
  CUTOVER_ENABLED: 'MOMS 컷오버 flag(feature.erpResult.mcisCutover)가 켜져 있어요. 끈 뒤 다시 판정하세요.',
  NEEDS_RECONCILIATION: '행 없음 · 컷오버 행 아님이 섞여 있어요. 일부만 실행하지 않아요. 원인을 MCIS 와 다시 대사한 뒤 목록을 고쳐 판정하세요.',
  NOTHING_ELIGIBLE: '실행 대상 행이 없어요. 이미 복원된 행만 있으면 할 일이 없어요.',
  IN_FLIGHT: '다른 요청이 진행 중이에요.',
} satisfies Record<ExecutionBlocker, string>

const BLOCKING_ROW_STATUSES: McisCutoverRollbackResendRowStatus[] = [ROW_STATUS.NOT_FOUND, ROW_STATUS.NOT_MCIS_CUTOVER]

export const hasBlockingRows = (rows: McisCutoverRollbackResendRow[]): boolean => rows.some((row) => BLOCKING_ROW_STATUSES.includes(row.status))

/**
 * 실행을 막는 이유. 빈 배열이어야 실행할 수 있다. 판정이 없으면 `null` — 막을 이유가 아니라 아직 할 수 없는 상태다.
 * `NOT_FOUND`·`NOT_MCIS_CUTOVER` 를 빼고 일부만 실행하는 길은 두지 않는다(런북: 원인을 설명하지 못하면 중단).
 */
export const executionBlockersOf = (
  judgement: ResendJudgement | null,
  currentInput: ResendInput | null,
  inFlight: boolean,
): ExecutionBlocker[] | null => {
  if (!judgement) return null
  const { response } = judgement
  const blockers: ExecutionBlocker[] = []
  if (!currentInput || !sameInput(judgement.input, currentInput)) blockers.push('STALE')
  if (!response.dryRun || response.outcome !== OUTCOME.DRY_RUN) blockers.push('NOT_DRY_RUN')
  if (response.cutoverEnabled || !response.executeAllowed) blockers.push('CUTOVER_ENABLED')
  const needsReconciliation = hasBlockingRows(response.rows)
  if (needsReconciliation) blockers.push('NEEDS_RECONCILIATION')
  // 재대사가 필요하면 "이미 복원된 행만 있다" 는 안내는 사실이 아니라 함께 내지 않는다.
  if (response.eligibleCount === 0 && !needsReconciliation) blockers.push('NOTHING_ELIGIBLE')
  if (inFlight) blockers.push('IN_FLIGHT')
  return blockers
}

export const countByRowStatus = (rows: McisCutoverRollbackResendRow[]): { status: McisCutoverRollbackResendRowStatus; count: number }[] =>
  ROW_STATUS_ORDER.map((status) => ({ status, count: rows.filter((row) => row.status === status).length })).filter((each) => each.count > 0)

/** 동시에 다른 경로가 행을 바꿨으면 판정 때 대상과 실제 갱신 건수가 어긋난다. */
export const releasedCountMismatch = (response: McisCutoverRollbackResendResponse): boolean =>
  response.outcome === OUTCOME.APPLIED && response.releasedCount !== response.eligibleCount

export const RESEND_FORBIDDEN_MESSAGE = '이 조치에 권한이 없어요. (MOMS_ENG 권한 필요)'
export const OUTCOME_UNKNOWN_MESSAGE =
  '응답을 받지 못했어요. 서버에서 실행이 끝났을 수 있어요. 같은 목록으로 다시 판정해 이미 복원됨으로 바뀌었는지 확인하세요. 다시 실행 버튼을 누르기 전에 꼭 판정하세요.'
export const JUDGE_NO_RESPONSE_MESSAGE = '판정 응답을 받지 못했어요. 판정은 아무것도 바꾸지 않으니 다시 판정하세요.'

const HTTP_CONFLICT = 409

const COUNT_FIELDS = ['requestedCount', 'eligibleCount', 'releasedCount'] as const

/**
 * 409 는 실패가 아니라 "컷오버 flag ON 이라 0건 변경" 을 담은 결과 본문이다. 요약이 그리는 필드까지 모양이 맞을 때만
 * 결과로 읽고, 아니면 일반 오류로 넘긴다.
 */
export const conflictResponseOf = (error: unknown): McisCutoverRollbackResendResponse | null => {
  if (httpStatusOf(error) !== HTTP_CONFLICT) return null
  const body = bodyOf(error) as Partial<McisCutoverRollbackResendResponse> | undefined
  const shaped =
    body?.outcome === OUTCOME.REJECTED_CUTOVER_ENABLED &&
    Array.isArray(body.rows) &&
    COUNT_FIELDS.every((field) => typeof body[field] === 'number') &&
    typeof body.operatorIdentity === 'string'
  return shaped ? (body as McisCutoverRollbackResendResponse) : null
}

export const interpretJudgeError = (error: unknown): PanelError =>
  isOutcomeUnknown(error) ? { kind: 'other', message: JUDGE_NO_RESPONSE_MESSAGE } : toPanelError(error, RESEND_FORBIDDEN_MESSAGE)

export const interpretExecuteError = (error: unknown): PanelError =>
  isOutcomeUnknown(error) ? { kind: 'other', message: OUTCOME_UNKNOWN_MESSAGE } : toPanelError(error, RESEND_FORBIDDEN_MESSAGE)
